import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../services/api'
import { jwtDecode } from 'jwt-decode'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const loading = ref(false)
  const error = ref(null)

  // Clear user data - defined before it's used
  const clearUserData = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    // apiClient sẽ tự động xử lý headers trong request interceptor
  }

  // Load user data and validate token on init
  const initAuth = () => {
    // Check for token and validate it if exists
    const storedToken = localStorage.getItem('auth_token')
    if (storedToken) {
      try {
        // Verify token is valid and not expired
        const decodedToken = jwtDecode(storedToken)
        const currentTime = Date.now() / 1000

        if (decodedToken.exp > currentTime) {
          // Token is valid, set it in the store
          token.value = storedToken

          // Now load user data if available
          const userData = localStorage.getItem('user_data')
          if (userData) {
            try {
              user.value = JSON.parse(userData)
            } catch (e) {
              console.error('Failed to parse user data:', e)
              localStorage.removeItem('user_data')
            }
          }
        } else {
          // Token is expired, clean everything
          console.log('Token expired during initialization')
          clearUserData()
        }
      } catch (e) {
        console.error('Token validation error on init:', e)
        clearUserData()
      }
    }
  }

  // Initialize auth data
  initAuth()

  // Set user data
  const setUser = (userData) => {
    user.value = userData
    localStorage.setItem('user_data', JSON.stringify(userData))
  }

  // Set authentication token
  const setToken = (tokenValue) => {
    token.value = tokenValue
    localStorage.setItem('auth_token', tokenValue)
    // API client sẽ tự động sử dụng token từ localStorage
  }

  // Login
  const login = async (credentials) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.login(credentials)

      console.log('API response:', response.data)
      console.log('Full API response structure:', JSON.stringify(response.data, null, 2))

      // Extract token and user info from response
      if (response.data && response.data.result) {
        const result = response.data.result;

        // Check for auth object structure based on API docs
        if (result && result.token) {
          setToken(result.token)
        } else {
          // Fallback: check if token is directly in the result
          if (result.token) {
            setToken(result.token)
          }
        }

        // Handle user data similarly with fallbacks
        if (result) {
          setUser(result)
        }
      } else {
        console.error('Response missing expected result structure', response.data)
      }

      return response.data
    } catch (err) {
      console.error('Login error:', err)
      const errorMessage = err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : 'Authentication failed';

      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      loading.value = false
    }
  }

  // Logout
  const logout = async () => {
    loading.value = true

    try {
      // Thêm endpoint logout khi backend có hỗ trợ
      // if (token.value) {
      //   await api.auth.logout()
      // }
    } catch (err) {
      console.error('Logout error:', err)
      // Still proceed with local logout even if server logout fails
    } finally {
      clearUserData()
      loading.value = false
    }
  }

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!token.value && checkTokenExpiration()
  }

  // Check if token is expired
  const checkTokenExpiration = () => {
    if (!token.value) return false;

    try {
      const decodedToken = jwtDecode(token.value);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        // Token is expired
        clearUserData();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      clearUserData();
      return false;
    }
  }

  // Get user profile
  const getProfile = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.getProfile();

      if (response.data && response.data.result) {
        const profileData = response.data.result;
        setUser(profileData);
        return profileData;
      } else {
        console.error('Failed to get profile data', response.data);
        error.value = 'Failed to retrieve profile data';
        return null;
      }
    } catch (err) {
      console.error('Get profile error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to retrieve profile';
      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      loading.value = false;
    }
  }

  // Update user profile
  const updateProfile = async (profileData) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.updateProfile(profileData);

      if (response.data && response.data.result) {
        const updatedProfile = response.data.result;
        setUser(updatedProfile);
        return { success: true, data: updatedProfile };
      } else {
        console.error('Failed to update profile', response.data);
        error.value = response.data?.message || 'Failed to update profile';
        return { success: false, error: error.value };
      }
    } catch (err) {
      console.error('Update profile error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  // Change password
  const changePassword = async (passwordData) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.changePassword(passwordData);

      if (response.data && response.data.result) {
        // Password successfully changed
        return { success: true, data: response.data.result };
      } else {
        console.error('Failed to change password', response.data);
        error.value = response.data?.message || 'Failed to change password';
        return { success: false, error: error.value };
      }
    } catch (err) {
      console.error('Change password error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to change password';
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  // Function to ensure token and user data are synchronized
  const refreshAuth = () => {
    const storedToken = localStorage.getItem('auth_token')

    // If token exists in localStorage but not in store, sync them
    if (storedToken && !token.value) {
      console.log('Refreshing auth: found token in localStorage but not in store')
      token.value = storedToken
    }

    // If user data exists in localStorage but not in store, load it
    if (!user.value) {
      const userData = localStorage.getItem('user_data')
      if (userData) {
        try {
          user.value = JSON.parse(userData)
          console.log('Refreshed user data from localStorage')
        } catch (e) {
          console.error('Failed to parse user data during refresh:', e)
        }
      }
    }

    return isAuthenticated()
  }

  return {
    user,
    token,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    checkTokenExpiration,
    setUser,
    setToken,
    getProfile,
    updateProfile,
    changePassword,
    refreshAuth
  }
})